// Pure-SVG doughnut whose segment labels sit on the ring (label + value placed
// at each arc's mid-angle), with the total shown in the centered hole.
import { Box, Flex, Text, useColorModeValue, useToken } from "@chakra-ui/react";

/**
 * Builds stacked stroke-dash arc descriptors for a doughnut ring.
 * @param {Array} data - segments ([{value, color}]) with resolved hex colors
 * @param {number} total - sum of values (0 yields no arcs)
 * @returns {Array} [{color, dash, offset}] for stacked SVG circles
 */
function buildArcs(data, total) {
  if (!total) return [];
  let cumulative = 0;
  return data.filter((d) => d.value > 0).map((d) => {
    const pct = (d.value / total) * 100;
    const arc = { color: d.color, dash: `${pct} ${100 - pct}`, offset: 25 - cumulative };
    cumulative += pct;
    return arc;
  });
}

/**
 * Computes a percent-based position for each segment label at its arc mid-angle.
 * @param {Array} data - segments ([{label, value, color}]) with resolved colors
 * @param {number} total - sum of values (0 yields no labels)
 * @param {number} radius - radial distance from center, in percent of half-box
 * @returns {Array} [{label, value, color, left, top}] with left/top in percent
 */
function buildLabels(data, total, radius) {
  if (!total) return [];
  let cumulative = 0;
  return data.filter((d) => d.value > 0).map((d) => {
    const mid = ((cumulative + d.value / 2) / total) * 360;
    const rad = ((mid - 90) * Math.PI) / 180;
    cumulative += d.value;
    return {
      label: d.label, value: d.value, color: d.color,
      left: 50 + radius * Math.cos(rad), top: 50 + radius * Math.sin(rad)
    };
  });
}

/**
 * Renders one absolutely-positioned arc label: a bold colored value over a
 * small muted caption, centered on its computed ring point.
 * @param {Object} props - label, value, color, left, top (left/top in percent)
 * @returns {React.ReactElement}
 */
function ArcLabel({ label, value, color, left, top }) {
  const captionColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Flex position="absolute" left={`${left}%`} top={`${top}%`} transform="translate(-50%, -50%)"
      direction="column" align="center" lineHeight="1.1" pointerEvents="none" maxW="68px">
      <Text fontSize="sm" fontWeight="800" color={color}>{value}</Text>
      <Text fontSize="9px" color={captionColor} noOfLines={1}>{label}</Text>
    </Flex>
  );
}

/**
 * Renders a doughnut with on-ring segment labels and a centered total.
 * @param {Object} props - data ([{label, value, color}]), total, centerLabel, size
 * @returns {React.ReactElement}
 */
export default function LabeledDonut({ data, total, centerLabel = "Total", size = 210 }) {
  const [trackLight, trackDark] = useToken("colors", ["secondaryGray.300", "navy.700"]);
  const track = useColorModeValue(trackLight, trackDark);
  const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const valueColor = useColorModeValue("secondaryGray.900", "white");
  const arcs = buildArcs(data, total);
  const labels = buildLabels(data, total, 47);
  return (
    <Box position="relative" w="100%" maxW={`${size}px`} mx="auto" sx={{ aspectRatio: "1 / 1" }}>
      <Box position="absolute" inset="23%">
        <Box as="svg" viewBox="0 0 36 36" w="100%" h="100%">
          <circle cx="18" cy="18" r="15.9155" fill="none" stroke={track} strokeWidth="4.5" />
          {arcs.map((a, i) => (
            <circle key={i} cx="18" cy="18" r="15.9155" fill="none" stroke={a.color} strokeWidth="4.5"
              strokeDasharray={a.dash} strokeDashoffset={a.offset} strokeLinecap="butt" />
          ))}
        </Box>
      </Box>
      <Flex position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
        direction="column" align="center" lineHeight="1" pointerEvents="none" zIndex="1">
        <Text fontSize="2xl" fontWeight="800" lineHeight="1" color={valueColor}>{total}</Text>
        <Text fontSize="xs" fontWeight="600" color={labelColor} mt="1px" lineHeight="1">
          {centerLabel}
        </Text>
      </Flex>
      {labels.map((l, i) => <ArcLabel key={i} {...l} />)}
    </Box>
  );
}
